import { Button, Space, Select, message, Modal, Input, Tag, Popconfirm } from 'antd'
import {
  AppstoreAddOutlined,
  FolderAddOutlined,
  CloseSquareOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { ActivityItem, FileFolderData, FilesData } from '../types/types'
import moment from 'moment'
import { ActivityItemFileWDate, ActivityItemsFiles } from '../../activity-timeline'
import { addFolder, clearFilesFolder, setFilesFolder } from '../api/fileFolders'
import { FileManagerFolder } from './file-manager-item'
import TextArea from 'antd/lib/input/TextArea'
import {
  addTimeLineFilesToFolder,
  clearTimeLineFilesFolder,
} from 'src/react-app/store/timeline/actions'
import Text from 'antd/lib/typography/Text'
import JSZip from 'jszip'
import { useAppDispatch } from '../../../../hooks/useAppDispatch/useAppDispatch'

const { Option } = Select

interface Props {
  activityItems: ActivityItemsFiles[]
}

// Предустановленные папки с временными отрицательными id
const PREDEFINED_FOLDERS: FileFolderData[] = [
  { id: -1, name: 'Вводные данные', description: '' },
  { id: -2, name: 'Расчёты', description: '' },
  { id: -3, name: 'Фото объекта ДО', description: '' },
  { id: -4, name: 'Замерные листы', description: '' },
  { id: -5, name: 'Пакет документов', description: '' },
  { id: -6, name: 'Фото объекта ПОСЛЕ', description: '' },
]

const FileManager: React.FC<Props> = ({ activityItems }) => {
  const AllFiles = activityItems.reduce((acc, { date, items }) => {
    items && acc.push(...items.map(i => ({ ...i, parentDate: date })))
    return acc
  }, [] as ActivityItem[]) as ActivityItemFileWDate[]

  const filesInFolders = AllFiles.reduce((acc, file) => {
    if (file.data && file.data.folder) {
      if (!acc[file.data.folder.id]) {
        acc[file.data.folder.id] = {
          folder: file.data.folder,
          files: [],
        }
      }
      acc[file.data.folder.id].files.push(file)
    }
    return acc
  }, {} as Record<string, { folder: FileFolderData; files: ActivityItemFileWDate[] }>)

  const [isCreateFolderModalVisible, setCreateFolderModalVisible] = useState(false)
  const [isAddToFolderModalVisible, setAddToFolderModalVisible] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<Map<number, ActivityItemFileWDate>>(new Map())
  const [newFolderName, setNewFolderName] = useState('')
  const [newFolderDesc, setNewFolderDesc] = useState('')
  const [folders, setFolders] = useState<FileFolderData[]>(PREDEFINED_FOLDERS)
  const [targetFolderId, setTargetFolderId] = useState<number | undefined>(undefined)
  const dispatch = useAppDispatch()

  useEffect(() => {
    setFolders(prevFolders => {
      const activityFolders = Object.values(filesInFolders).map(i => i.folder)
      const allFolders = [...prevFolders.filter(f => f.id < 0), ...activityFolders]
      const uniqueFolders = Array.from(
        new Map(allFolders.map(folder => [folder.id, folder])).values(),
      )
      return uniqueFolders
    })
  }, [activityItems])

  const handleFileSelectionChange = (fileId: number, activity: ActivityItemFileWDate) => {
    setSelectedFiles(prevSelectedFiles => {
      const tmp = new Map(prevSelectedFiles)
      if (tmp.has(fileId)) {
        tmp.delete(fileId)
      } else {
        tmp.set(fileId, activity)
      }
      return tmp
    })
  }

  const handleDownload = async () => {
    if (selectedFiles.size === 0) {
      message.warning('Выберите файлы для скачивания')
      return
    }

    const selectedArray = Array.from(selectedFiles.values())

    if (selectedArray.length === 1) {
      const file = selectedArray[0]
      const fileData = file.data as { files: FilesData[] }
      if (fileData.files[0]) {
        const url = fileData.files[0].url
        window.open(url, '_blank')
      }
    } else {
      const zip = new JSZip()

      try {
        await Promise.all(
          selectedArray.map(async file => {
            const fileData = file.data as { files: FilesData[] }
            if (!fileData.files[0]) return

            const url = fileData.files[0].url
            const name = fileData.files[0].name || 'без_названия'

            const response = await fetch(url)
            if (!response.ok) {
              console.log('Не удалось загрузить файл')
            }

            const blob = await response.blob()
            zip.file(name, blob)
          }),
        )

        const content = await zip.generateAsync({ type: 'blob' })
        const { saveAs } = await import('file-saver')
        saveAs(content, `архив_${moment().format('DD-MM-YYYY_HH-mm-ss')}.zip`)
      } catch (error) {
        message.error('Ошибка при создании архива')
      }
    }
  }

  const showCreateFolderModal = () => {
    setCreateFolderModalVisible(true)
    setNewFolderName('')
    setNewFolderDesc('')
  }

  const handleCreateFolderConfirm = () => {
    if (newFolderName.trim() === '') {
      message.error('Название папки не должно быть пустым')
      return
    }

    addFolder({ folder_name: newFolderName, folder_description: newFolderDesc })
      .then(res => {
        console.log(res)
        if (!res) {
          throw new Error('Ошибка сервера')
        }
        const newId = res['folder_id']
        if (!newId) {
          throw new Error('сервер не вернул folder_id')
        }
        // TODO создавать новую папку на беке только при добавлении в неё файлов
        setFolders(prevFolders => [
          ...prevFolders,
          { id: newId, name: newFolderName, description: newFolderDesc || undefined },
        ])
        setCreateFolderModalVisible(false)
        message.success(`Папка "${newFolderName}" создана`)
      })
      .catch(() => {
        message.error(`Не удалось создать папку`)
      })
  }

  const handleCreateFolderCancel = () => {
    setCreateFolderModalVisible(false)
    setNewFolderName('')
    setNewFolderDesc('')
  }

  const showAddToFolderModal = () => {
    if (selectedFiles.size === 0) {
      message.warning('Выберите файлы которые хотите добавить в папку')
      return
    }
    if (folders.length === 0) {
      message.warning('Нужно создать хотя бы одну папку.')
      return
    }
    setAddToFolderModalVisible(true)
    setTargetFolderId(undefined)
  }

  const handleAddToFolderConfirm = useCallback(async () => {
    if (!targetFolderId) {
      message.error('Выберите папку')
      return
    }

    let finalFolderId = targetFolderId
    let targetFolder = folders.find(el => el.id === targetFolderId)

    if (targetFolderId < 0 && targetFolder) {
      try {
        const response: any = await addFolder({
          folder_name: targetFolder.name,
          folder_description: targetFolder.description,
        })
        if (!response?.folder_id) {
          throw new Error(`Не удалось создать папку "${targetFolder.name}"`)
        }
        finalFolderId = response.folder_id

        setFolders(prevFolders =>
          prevFolders.map(folder =>
            folder.id === targetFolderId
              ? {
                  id: finalFolderId,
                  name: targetFolder!.name,
                  description: targetFolder!.description,
                }
              : folder,
          ),
        )
        targetFolder = {
          id: finalFolderId,
          name: targetFolder.name,
          description: targetFolder.description,
        }
      } catch (error) {
        console.error(error)
        message.error(`Не удалось создать папку "${targetFolder.name}"`)
        return
      }
    }

    if (!targetFolder) {
      message.error('Папка не найдена')
      return
    }

    setFilesFolder({ files_ids: Array.from(selectedFiles.keys()), folder_id: finalFolderId })
      .then(res => {
        if (!res) {
          throw new Error('Ошибка сервера')
        }
        dispatch(addTimeLineFilesToFolder(Array.from(selectedFiles.values()), targetFolder!))
        setAddToFolderModalVisible(false)
        setSelectedFiles(new Map())
        setTargetFolderId(undefined)
        message.success(`Файлы добавлены в папку "${targetFolder!.name}"`)
      })
      .catch(() => {
        message.error(`Не удалось добавить файлы в папку`)
      })
  }, [selectedFiles, targetFolderId, folders])

  const handleAddToFolderCancel = () => {
    setAddToFolderModalVisible(false)
    setTargetFolderId(undefined)
  }

  const handleDeleteFromFolderConfirm = () => {
    if (selectedFiles.size === 0) {
      message.warning('Выберите файлы которые хотите удалить')
      return
    }
    clearFilesFolder({ files_ids: Array.from(selectedFiles.keys()) })
      .then(res => {
        if (!res) {
          throw new Error('Ошибка сервера')
        }
        dispatch(clearTimeLineFilesFolder(Array.from(selectedFiles.values())))
        setSelectedFiles(new Map())
        message.success(`Файлы удалены из папок`)
      })
      .catch(() => {
        message.error(`Не удалось удалить файлы из папки`)
      })
  }

  return (
    <div>
      <Space style={{ marginBottom: '1rem' }} wrap size={[8, 8]}>
        <Button type="primary" icon={<FolderAddOutlined />} onClick={showCreateFolderModal}>
          Создать папку
        </Button>
        <Button
          type="primary"
          icon={<AppstoreAddOutlined />}
          onClick={showAddToFolderModal}
          disabled={selectedFiles.size === 0}
        >
          Добавить в папку
        </Button>
        <Popconfirm
          title="Вы точно хотите удалить файлы из папки?"
          onConfirm={handleDeleteFromFolderConfirm}
          disabled={selectedFiles.size === 0}
        >
          <Button danger icon={<CloseSquareOutlined />} disabled={selectedFiles.size === 0}>
            Удалить из папки
          </Button>
        </Popconfirm>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          disabled={selectedFiles.size === 0}
        >
          Скачать
        </Button>
      </Space>

      <FileManagerFolder
        onSelect={handleFileSelectionChange}
        files={AllFiles}
        folderName={'Все файлы'}
        showFolderTag={true}
        selectedFiles={selectedFiles}
      />

      {Object.values(filesInFolders).map(({ folder, files }) => (
        <FileManagerFolder
          folderName={folder.name}
          onSelect={handleFileSelectionChange}
          files={files}
          description={folder.description}
          key={folder.id}
          selectedFiles={selectedFiles}
        />
      ))}

      <Modal
        title="Добавить новую папку"
        open={isCreateFolderModalVisible}
        onOk={handleCreateFolderConfirm}
        onCancel={handleCreateFolderCancel}
        okText="Создать"
        cancelText="Отмена"
      >
        <Space direction="vertical" style={{ display: 'flex' }}>
          <Input
            placeholder="Название новой папки"
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
          />
          <TextArea
            placeholder="Описание новой папки (необязательно)"
            value={newFolderDesc}
            onChange={e => setNewFolderDesc(e.target.value)}
          />
        </Space>
      </Modal>

      <Modal
        title="Добавить файлы в папку"
        open={isAddToFolderModalVisible}
        onOk={handleAddToFolderConfirm}
        onCancel={handleAddToFolderCancel}
        okText="Добавить"
        cancelText="Отмена"
      >
        <Space direction="vertical" style={{ display: 'flex' }}>
          <Select
            style={{ width: '100%' }}
            placeholder="Выберите папку"
            onChange={value => setTargetFolderId(value)}
            value={targetFolderId}
          >
            {folders
              .filter(folder => {
                if (folder.id < 0) {
                  return !folders.some(
                    existingFolder =>
                      existingFolder.id >= 0 &&
                      existingFolder.name.toLowerCase() === folder.name.toLowerCase(),
                  )
                }
                return true
              })
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(folder => (
                <Option key={folder.id} value={folder.id}>
                  {folder.name}
                </Option>
              ))}
          </Select>
          <Text type="secondary">Важно! Если папка останется пустой - она удалится</Text>
        </Space>
      </Modal>
    </div>
  )
}

export default FileManager
